import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Rfid.css';

// RFID Reader console (web stand-in for the Android rfid_reader_activity.xml).
// Most USB/proximity RFID readers act as a keyboard wedge: they "type" the tag
// id and press Enter. This page keeps a capture field focused so those reads
// land here, logs each scan with a timestamp, and also accepts manual entry for
// testing. It's a diagnostic console — it verifies a reader works and lets you
// read tag ids; it doesn't yet map tags to products or customers.

interface Scan {
  id: string;
  tag: string;
  at: Date;
}

const MAX_SCANS = 500;

const Rfid: React.FC = () => {
  const [scans, setScans] = useState<Scan[]>([]);
  const [buffer, setBuffer] = useState('');
  const [focused, setFocused] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const focus = () => inputRef.current?.focus();
  useEffect(() => {
    focus();
  }, []);

  const record = useCallback((raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    setScans((prev) =>
      [{ id: `${Date.now()}-${prev.length}`, tag, at: new Date() }, ...prev].slice(0, MAX_SCANS),
    );
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Keyboard-wedge readers terminate each read with Enter; also lets a person
    // type a tag by hand and press Enter to log it.
    if (e.key === 'Enter') {
      e.preventDefault();
      record(buffer);
      setBuffer('');
    }
  };

  const last = scans[0];
  const uniqueCount = new Set(scans.map((s) => s.tag)).size;

  const copyLast = async () => {
    if (!last) return;
    try {
      await navigator.clipboard.writeText(last.tag);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  const time = (d: Date) => d.toLocaleTimeString(undefined, { hour12: false });

  return (
    <div className="rfid-page">
      <div className="page-header">
        <h1>RFID Reader</h1>
      </div>

      <div className="rfid-grid">
        {/* capture + last read */}
        <div className="rfid-card">
          <div className="rfid-reader">
            <span className={`rfid-status ${focused ? 'on' : 'off'}`}>
              <span className="rfid-dot" />
              {focused ? 'Listening for scans' : 'Paused — click to resume'}
            </span>

            <div className="rfid-icon">📡</div>

            {last ? (
              <>
                <div className="rfid-last-label">Last tag</div>
                <div className="rfid-last-tag" title={last.tag}>{last.tag}</div>
                <div className="rfid-last-time">at {time(last.at)}</div>
              </>
            ) : (
              <p className="rfid-hint">
                Scan a tag with your RFID reader, or type an id below and press Enter.
                Most readers act as a keyboard — just keep this page open and scan.
              </p>
            )}

            <input
              ref={inputRef}
              className="rfid-input"
              value={buffer}
              onChange={(e) => setBuffer(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="Scan or type a tag id, then Enter"
              autoComplete="off"
              spellCheck={false}
            />

            <div className="rfid-actions">
              <button
                className="rfid-btn rfid-btn-primary"
                onClick={() => {
                  record(buffer);
                  setBuffer('');
                  focus();
                }}
                disabled={!buffer.trim()}
              >
                Add
              </button>
              <button className="rfid-btn rfid-btn-ghost" onClick={copyLast} disabled={!last}>
                {copied ? 'Copied ✓' : 'Copy last'}
              </button>
            </div>
          </div>
        </div>

        {/* scan log */}
        <div className="rfid-card">
          <div className="rfid-log-head">
            <span>Scan log</span>
            <span className="rfid-log-stats">
              {scans.length} scan{scans.length === 1 ? '' : 's'} · {uniqueCount} unique
            </span>
            <button
              className="rfid-btn rfid-btn-ghost rfid-btn-sm"
              onClick={() => {
                setScans([]);
                focus();
              }}
              disabled={scans.length === 0}
            >
              Clear
            </button>
          </div>

          <div className="rfid-log">
            {scans.length === 0 ? (
              <p className="rfid-empty">No scans yet.</p>
            ) : (
              scans.map((s) => (
                <div key={s.id} className="rfid-log-row">
                  <span className="rfid-log-tag">{s.tag}</span>
                  <span className="rfid-log-time">{time(s.at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rfid;
